import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { orderService } from '../services/orderService';
import { Order } from '../services/adminService';
import { formatPrice, formatDateTime } from '../utils';
import Badge from '../components/UI/Badge';
import Breadcrumbs from '../components/UI/Breadcrumbs';

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) return;
    
    const response = await orderService.getOrder(orderId);
    if (response.success && response.data) {
      setOrder(response.data);
    }
    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'text-yellow-600 bg-yellow-50',
      confirmed: 'text-blue-600 bg-blue-50',
      processing: 'text-purple-600 bg-purple-50',
      shipped: 'text-indigo-600 bg-indigo-50',
      delivered: 'text-green-600 bg-green-50',
      cancelled: 'text-red-600 bg-red-50',
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const orderSteps = [
    { status: 'pending', label: 'Order Placed', icon: '📝' },
    { status: 'confirmed', label: 'Confirmed', icon: '✓' },
    { status: 'processing', label: 'Processing', icon: '📦' },
    { status: 'shipped', label: 'Shipped', icon: '🚚' },
    { status: 'delivered', label: 'Delivered', icon: '✅' },
  ];

  const currentStepIndex = orderSteps.findIndex(s => s.status === order?.status);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-600">Loading order...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <span className="text-6xl block mb-4">📦</span>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <Link to="/orders" className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium inline-block">
            View All Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[
          { label: 'Home', path: '/' },
          { label: 'Orders', path: '/orders' },
          { label: `Order #${order._id.slice(-8)}` },
        ]} />

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Order #{order._id.slice(-8).toUpperCase()}</h1>
            <p className="text-gray-600 mt-1">{formatDateTime(order.createdAt)}</p>
          </div>
          <div className="flex space-x-2">
            <Badge variant={order.status === 'delivered' ? 'success' : 'info'}>{order.status}</Badge>
            <Badge variant={order.paymentStatus === 'paid' ? 'success' : 'warning'}>{order.paymentStatus}</Badge>
          </div>
        </div>

        {/* Order Progress */}
        {order.status !== 'cancelled' && (
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Order Progress</h2>
            <div className="flex items-center justify-between">
              {orderSteps.map((step, index) => (
                <div key={step.status} className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                    index <= currentStepIndex ? getStatusColor(step.status) : 'bg-gray-100 text-gray-400'
                  }`}>
                    {step.icon}
                  </div>
                  <p className={`text-xs mt-2 font-medium ${
                    index <= currentStepIndex ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </p>
                  {index < orderSteps.length - 1 && (
                    <div className={`h-1 w-full mt-6 ${
                      index < currentStepIndex ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Items */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Order Items</h2>
              <div className="space-y-4">
                {order.products.map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-4 pb-4 border-b border-gray-200">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-3xl">
                      📦
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {typeof item.product === 'object' ? item.product.name : 'Product'}
                      </h3>
                      <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                      <p className="text-sm text-gray-600">{formatPrice(item.price)} each</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary & Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700">Subtotal</span>
                  <span className="font-medium">{formatPrice(order.totalAmount)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-green-600">{formatPrice(order.totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-2xl shadow-sm p-6 space-y-3">
              <Link
                to="/orders"
                className="block w-full py-3 bg-gray-100 text-gray-700 text-center rounded-lg hover:bg-gray-200 transition-all font-medium"
              >
                ← Back to Orders
              </Link>
              {order.status === 'delivered' && (
                <button className="block w-full py-3 bg-green-600 text-white text-center rounded-lg hover:bg-green-700 transition-all font-medium">
                  Reorder
                </button>
              )}
              <a
                href="tel:+254700000000"
                className="block w-full py-3 border-2 border-green-600 text-green-600 text-center rounded-lg hover:bg-green-50 transition-all font-medium"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;

